// pages/team/index.jsx
// 📍 Added canAppAccess toggle

import React, { useEffect, useState } from "react";
import {
  Card,
  Form,
  Input,
  Button,
  Radio,
  Drawer,
  Table,
  DatePicker,
  Spin,
  Select,
  Tag,
  Space,
  Switch,
  Tooltip,
   message
} from "antd";
import { 
  EditOutlined, 
  DeleteOutlined, 
  EyeOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import {
  useCreateTeamMemberMutation,
  useDeleteTeamMemberMutation,
  useFetchTeamMembersQuery,
  useUpdateTeamMemberMutation,
} from "./team.service";
import { useFetchMyLocationsQuery } from "../restaurant/restaruant.service";
import moment from "moment/moment";
import { useTranslation } from "react-i18next";
import "./style.css";
import { useNavigate } from "react-router-dom";
import { trainingLevel } from "../../constant";
import { useSelector } from "react-redux";

const { Column } = Table;

const options = [
  { value: "level1", label: "Level 1" },
  { value: "level2", label: "Level 2" },
  { value: "level3", label: "Level 3" },
];

export default function Team() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [isDrawer, setIsDrawer] = useState(false);
  const [isToolLoading, setIsToolLoading] = useState("");
  const [isEditable, setIsEditable] = useState();
  
  const user = useSelector((state) => state.app.user);
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  
  const { data: locationsData } = useFetchMyLocationsQuery();
  const locations = locationsData?.data?.locations || [];
  const activeLocationId = locationsData?.data?.activeLocation || user?.activeLocationId;

  // ✅ Fetch all members
  const { data = { data: { members: [] } }, isLoading, refetch } = useFetchTeamMembersQuery();
  const { data: { members } } = data;

  const [requestCreateTeamMember, { isSuccess, isLoading: isCreateLoading }] =
    useCreateTeamMemberMutation();
  const [requestUpdateTeamMember, { isSuccess: isUpdateSuccess }] =
    useUpdateTeamMemberMutation();
  const [requestDeleteTeamMember, { isSuccess: isDeleteSuccess }] =
    useDeleteTeamMemberMutation();

  const handleDrawer = () => {
    if (activeLocationId) {
      form.setFieldsValue({ restaurantId: activeLocationId });
    }
    setIsDrawer(true);
  };

  const handleDeleteTeamMember = async (id) => {
    setIsToolLoading(id);
    await requestDeleteTeamMember(id);
    setIsToolLoading("");
    refetch();
  };

  const handleUpdateTeamMember = (data) => {
    setIsEditable(data._id);
    setIsDrawer(true);
    form.setFieldsValue({
      name: data.name,
      dateOfBirth: moment(data.dateOfBirth),
      email: data.email,
      mobileNumber: data.mobileNumber,
      trainingLevel: data.trainingLevel,
      hygieneTraining: data.hygieneTraining,
      allergenTraining: data.allergenTraining,
      restaurantId: data.restaurantId,
      locationIds:
        data.locationIds?.length > 0
          ? data.locationIds
          : data.restaurantId
          ? [data.restaurantId]
          : [],
      canAppAccess: data.canAppAccess || false, // ✅ ADD THIS
    });
  };

  useEffect(() => {
    if (!isDrawer) {
      form.resetFields();
      setIsEditable();
    }
  }, [isDrawer]);

  useEffect(() => {
    if (isSuccess || isUpdateSuccess || isDeleteSuccess) {
      refetch();
    }
  }, [isSuccess, isUpdateSuccess, isDeleteSuccess]);

  const handleFormSubmit = (data) => {
    const locationIds =
      Array.isArray(data.locationIds) && data.locationIds.length > 0
        ? data.locationIds
        : data.restaurantId
        ? [data.restaurantId]
        : [];
    const payload = {
      ...data,
      userLevel: "level1",
      restaurantId: data.restaurantId || locationIds[0],
      locationIds,
    };
    
    if (isEditable) {
      requestUpdateTeamMember({
        id: isEditable,
        data: payload,
      });
    } else {
      requestCreateTeamMember(payload);
    }
    setIsDrawer(false);
  };

  const handleView = (id) => {
    navigate("/team/view/" + id);
  };

  // ✅ Handle toggle canAppAccess
  const handleToggleAppAccess = async (record) => {
    try {
      setIsToolLoading(record._id);
      await requestUpdateTeamMember({
        id: record._id,
        data: {
          ...record,
          canAppAccess: !record.canAppAccess,
          userLevel: "level1",
        },
      }).unwrap();
      refetch();
      message.success(
        `App access ${!record.canAppAccess ? 'enabled' : 'disabled'} for ${record.name}`
      );
    } catch (error) {
      message.error("Failed to update app access");
    } finally {
      setIsToolLoading("");
    }
  };

  // ✅ Get location name by ID
  const getLocationName = (locationId) => {
    if (!locationId) return 'No Location';
    const loc = locations.find(l => l._id === locationId);
    return loc ? loc.name : 'Unknown Location';
  };

  // ✅ Get location color
  const getLocationColor = (locationId) => {
    if (!locationId) return 'default';
    const colors = ['blue', 'green', 'purple', 'orange', 'cyan', 'magenta', 'red', 'gold'];
    const index = locationId.toString().length % colors.length;
    return colors[index];
  };

  return (
    <Card
      className="team-page"
      bordered={false}
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '18px', fontWeight: 'bold' }}>
            👥 {t("TEAM MEMBERS LIST")}
          </span>
          
          <Tag color="green" style={{ fontSize: '14px', padding: '4px 12px' }}>
            Total: {members.length} members
          </Tag>
        </div>
      }
      extra={
        <Button type="primary" onClick={handleDrawer} icon={<EditOutlined />}>
          {t("add new team member")}
        </Button>
      }
    >
      <Spin spinning={isLoading}>
        <div className="team-page-container">
          <div className="team-page-container-content">
            
            <Table
              dataSource={members}
              pagination={{ current: 1, pageSize: 10 }}
              rowKey="_id"
              bordered
            >
              <Column 
                align="center" 
                title={t("#")} 
                render={(_, __, index) => index + 1}
                width={50}
              />
              
              <Column 
                align="center" 
                title={t("NAME")} 
                dataIndex={"name"}
                render={(name) => <strong>{name}</strong>}
              />
              
              <Column 
                align="center" 
                title={t("EMAIL ADDRESS")} 
                dataIndex={"email"} 
              />
              
              <Column
                align="center"
                title={t("LOCATION")}
                dataIndex={"restaurantId"}
                render={(restaurantId, record) => {
                  const ids =
                    Array.isArray(record.locationIds) && record.locationIds.length > 0
                      ? record.locationIds
                      : restaurantId
                      ? [restaurantId]
                      : [];
                  const locationName = getLocationName(restaurantId);
                  const isActive = restaurantId === activeLocationId;
                  const color = getLocationColor(restaurantId);
                  
                  return (
                    <Space wrap>
                      <Tag color={color} style={{ fontSize: '13px', padding: '4px 12px' }}>
                        📍 {locationName}
                      </Tag>
                      {ids.length > 1 && (
                        <Tag color="purple" style={{ fontSize: '11px' }}>
                          +{ids.length - 1} more
                        </Tag>
                      )}
                      {isActive && (
                        <Tag color="green" style={{ fontSize: '11px' }}>
                          Active
                        </Tag>
                      )}
                      {!restaurantId && (
                        <Tag color="red" style={{ fontSize: '11px' }}>
                          ⚠️ No Location
                        </Tag>
                      )}
                    </Space>
                  );
                }}
              />
              
              {/* ✅ APP ACCESS COLUMN with Toggle */}
              <Column
                align="center"
                title={t("APP ACCESS")}
                dataIndex={"canAppAccess"}
                render={(canAppAccess, record) => (
                  <Space>
                    <Tooltip title={canAppAccess ? "Access Enabled" : "Access Disabled"}>
                      <Switch
                        checked={canAppAccess}
                        onChange={() => handleToggleAppAccess(record)}
                        loading={isToolLoading === record._id}
                        checkedChildren={<CheckCircleOutlined />}
                        unCheckedChildren={<CloseCircleOutlined />}
                      />
                    </Tooltip>
                    <Tag color={canAppAccess ? "green" : "red"} style={{ fontSize: '11px' }}>
                      {canAppAccess ? "Active" : "Inactive"}
                    </Tag>
                  </Space>
                )}
              />
              
              <Column
                align="center"
                title={t("TRAINING LEVEL")}
                dataIndex={"trainingLevel"}
                render={(level) => {
                  const color = level === 'level3' ? 'gold' : level === 'level2' ? 'blue' : 'green';
                  return <Tag color={color}>{trainingLevel[level]}</Tag>;
                }}
              />
              
              <Column
                title={t("TOOLS")}
                align="center"
                width={250}
                render={(value, record) => (
                  <Space size="small">
                    <Button
                      type="default"
                      variant="solid"
                      color="cyan"
                      icon={<EyeOutlined />}
                      onClick={() => handleView(record._id)}
                      size="small"
                    >
                      {t("View")}
                    </Button>

                    <Button
                      color="green"
                      variant="solid"
                      icon={<EditOutlined />}
                      size="small"
                      isLoading={isToolLoading === record._id && !record.canAppAccess}
                      onClick={() => handleUpdateTeamMember(record)}
                    >
                      {t("Edit")}
                    </Button>
                    
                    <Button
                      variant="solid"
                      color="danger"
                      size="small"
                      icon={<DeleteOutlined />}
                      onClick={() => handleDeleteTeamMember(record._id)}
                      isLoading={isToolLoading === record._id}
                    >
                      {t("DELETE")}
                    </Button>
                  </Space>
                )}
              />
            </Table>

            {/* If no members */}
            {members.length === 0 && !isLoading && (
              <div style={{ textAlign: 'center', padding: '60px', color: '#999' }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>👥</div>
                <div style={{ fontSize: '18px' }}>No team members found</div>
                <div style={{ fontSize: '14px', marginTop: '8px' }}>
                  Click <strong>"Add"</strong> to create your first team member
                </div>
              </div>
            )}
          </div>

          {/* Drawer */}
          <Drawer
            title={isEditable ? t("Edit team member") : t("Add new team member")}
            width={"70%"}
            open={isDrawer}
            onClose={() => {
              form.resetFields();
              setIsDrawer(false);
            }}
          >
            <Form
              form={form}
              layout="vertical"
              onFinish={handleFormSubmit}
              initialValues={{
                name: "",
                birthday: "",
                email: "",
                mobile_number: "",
                user_level: "",
                training_level: "",
                hygiene_training: "",
                allergen_training: "",
                restaurantId: activeLocationId,
                locationIds: activeLocationId ? [activeLocationId] : [],
                canAppAccess: false, // ✅ Default false
              }}
            >
              {/* Location Selector — primary + optional multi-site access */}
              {isAdmin && (
                <>
                  <div className="team-page-container-form">
                    <div className="team-page-container-form-label">
                      {t("LOCATION")} <span style={{ color: 'red' }}>*</span>
                      <div style={{ color: "#8c8c8c", fontSize: "12px", marginBottom: "6px" }}>
                        Primary location for this team member
                      </div>
                    </div>
                    <div className="team-page-container-form-input">
                      <Form.Item
                        name="restaurantId"
                        className="team-page-container-form-input-width"
                        rules={[
                          {
                            required: true,
                            message: "Please select a location",
                          },
                        ]}
                      >
                        <Select
                          placeholder={t("Select Location")}
                          style={{ width: "100%" }}
                          onChange={(value) => {
                            const current = form.getFieldValue("locationIds") || [];
                            if (!current.includes(value)) {
                              form.setFieldsValue({
                                locationIds: [...current, value],
                              });
                            }
                          }}
                        >
                          {locations.map(loc => (
                            <Select.Option key={loc._id} value={loc._id}>
                              {loc.name}
                              {loc._id === activeLocationId && " (Active)"}
                            </Select.Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </div>
                  </div>
                  <div className="team-page-container-form">
                    <div className="team-page-container-form-label">
                      Additional locations
                      <div style={{ color: "#8c8c8c", fontSize: "12px", marginBottom: "6px" }}>
                        Optional — allow this staff member to switch between multiple sites
                      </div>
                    </div>
                    <div className="team-page-container-form-input">
                      <Form.Item
                        name="locationIds"
                        className="team-page-container-form-input-width"
                      >
                        <Select
                          mode="multiple"
                          placeholder="Select one or more locations"
                          style={{ width: "100%" }}
                          allowClear
                        >
                          {locations.map(loc => (
                            <Select.Option key={loc._id} value={loc._id}>
                              {loc.name}
                            </Select.Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </div>
                  </div>
                </>
              )}

              {/* Hidden field for non-admin */}
              {!isAdmin && (
                <Form.Item name="restaurantId" hidden>
                  <Input />
                </Form.Item>
              )}

              {/* ✅ APP ACCESS TOGGLE in Form */}
              <div className="team-page-container-form">
                <div className="team-page-container-form-label">
                  {t("APP ACCESS")}
                  <div style={{ color: "#8c8c8c", fontSize: "12px", marginBottom: "6px" }}>
                    Enable/disable member's access to the mobile app
                  </div>
                </div>
                <div className="team-page-container-form-input">
                  <Form.Item
                    name="canAppAccess"
                    valuePropName="checked"
                  >
                    <Switch
                      checkedChildren="Enabled"
                      unCheckedChildren="Disabled"
                    />
                  </Form.Item>
                </div>
              </div>

              <div className="team-page-container-form">
                <div className="team-page-container-form-label">
                  {t("NAME")} <span style={{ color: 'red' }}>*</span>
                </div>
                <div className="team-page-container-form-input">
                  <Form.Item
                    name="name"
                    className="team-page-container-form-input-width"
                    rules={[
                      {
                        required: true,
                        message: "Please enter a name",
                      },
                    ]}
                  >
                    <Input />
                  </Form.Item>
                </div>
              </div>

              <div className="team-page-container-form">
                <div className="team-page-container-form-label">
                  {t("DATE OF BIRTH")} <span style={{ color: 'red' }}>*</span>
                </div>
                <div className="team-page-container-form-input">
                  <Form.Item
                    name="dateOfBirth"
                    className="team-page-container-form-input-width"
                    rules={[
                      {
                        required: true,
                        message: "Please enter a birthday",
                      },
                    ]}
                  >
                    <DatePicker
                      placeholder=""
                      style={{ width: "100%" }}
                      format={"YYYY-MM-DD"}
                    />
                  </Form.Item>
                </div>
              </div>

              <div className="team-page-container-form">
                <div className="team-page-container-form-label">
                  {t("EMAIL")} <span style={{ color: 'red' }}>*</span>
                  <div style={{ color: "#8c8c8c", fontSize: "12px", marginBottom: "6px" }}>
                    If email is added user will be sent a link to signup for their own app access (app only)
                  </div>
                </div>
                <div className="team-page-container-form-input">
                  <Form.Item
                    name="email"
                    className="team-page-container-form-input-width"
                    rules={[
                      {
                        required: true,
                        message: "Please enter an email",
                        type: "email",
                      },
                    ]}
                  >
                    <Input />
                  </Form.Item>
                </div>
              </div>

              <div className="team-page-container-form">
                <div className="team-page-container-form-label">
                  {t("MOBILE NUMBER")} <span style={{ color: 'red' }}>*</span>
                </div>
                <div className="team-page-container-form-input">
                  <Form.Item
                    name="mobileNumber"
                    className="team-page-container-form-input-width"
                    rules={[
                      {
                        required: true,
                        message: "Please enter an mobile number",
                      },
                    ]}
                  >
                    <Input />
                  </Form.Item>
                </div>
              </div>

              <div className="team-page-container-form">
                <div className="team-page-container-form-label">
                  {t("TRAINING LEVEL")} <span style={{ color: 'red' }}>*</span>
                </div>
                <div className="team-page-container-form-input">
                  <Form.Item
                    name="trainingLevel"
                    className="team-page-container-form-input-width"
                    rules={[
                      {
                        required: true,
                        message: "Please enter an training level",
                      },
                    ]}
                  >
                    <Select options={options} />
                  </Form.Item>
                </div>
              </div>

              <div className="team-page-container-form">
                <div className="team-page-container-form-label">
                  {t("HYGIENE TRAINING")} <span style={{ color: 'red' }}>*</span>
                </div>
                <div className="team-page-container-form-input team-page-container-form-input-radio">
                  <Form.Item
                    name="hygieneTraining"
                    rules={[
                      {
                        required: true,
                        message: "Please enter a hygiene training",
                      },
                    ]}
                  >
                    <Radio.Group
                      options={[
                        { label: t("YES"), value: true },
                        { label: t("NO"), value: false },
                      ]}
                      optionType="button"
                      buttonStyle="solid"
                    />
                  </Form.Item>
                  <div className="team-page-container-form-input-tag">
                    {t("SEND FORMS")}
                  </div>
                </div>
              </div>

              <div className="team-page-container-form">
                <div className="team-page-container-form-label">
                  {t("ALLERGEN TRAINING")} <span style={{ color: 'red' }}>*</span>
                </div>
                <div className="team-page-container-form-input team-page-container-form-input-radio">
                  <Form.Item
                    name="allergenTraining"
                    rules={[
                      {
                        required: true,
                        message: "Please enter an allergen training",
                      },
                    ]}
                  >
                    <Radio.Group
                      options={[
                        { label: t("YES"), value: true },
                        { label: t("NO"), value: false },
                      ]}
                      optionType="button"
                      buttonStyle="solid"
                    />
                  </Form.Item>
                  <div className="team-page-container-form-input-tag">
                    {t("SEND FORMS")}
                  </div>
                </div>
              </div>

              <div className="team-page-container-form-button">
                <Button type="primary" htmlType="submit" loading={isCreateLoading} size="large">
                  {isEditable ? t("UPDATE") : t("CREATE")}
                </Button>
              </div>
            </Form>
          </Drawer>
        </div>
      </Spin>
    </Card>
  );
}